import { useState } from "react";
import { Alert, Button, Skeleton, Stack, TextInput } from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { SmsGroupTemplateType, SmsTemplateStatus, SmsType } from "@/models/club";
import smsTemplateService from "@/services/template";
import { SmsTemplate } from "@/services/discount/type";
import { PlaceHolder } from "@/services/smsPlaceHolders/type";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import { useSmartAudienceTemplates } from "@/services/smartAudience/hooks";
import { withRecipientSamples } from "@/modules/smartAudience/text";
import MessageText, { smsPartsOf } from "./MessageText";
import classes from "./campaign.module.css";

const fa = (value: number) => value.toLocaleString("fa-IR");

// Placeholder samples for previews; a smart audience's per-customer ones get sample values too.
function usePreviewTags(smartService?: string | null) {
  const placeholders = useGetSmsPlaceHolder();
  const data = placeholders.data ?? [];
  return smartService === undefined ? data : withRecipientSamples(data, smartService);
}

// "۲ پیامک برای هر نفر" for the chosen text, shown beside the continue button.
export function useMessageParts(text?: string | null, smartService?: string | null) {
  const tags = usePreviewTags(smartService);
  return text ? `${fa(smsPartsOf(text, tags))} پیامک برای هر نفر` : undefined;
}

// `audienceKey`: a smart audience was chosen, so its own templates are offered (recommended first) instead of the groups.
export default function TemplatePicker({ smsType, value, onChange, audienceKey, serviceTitle }: { smsType: SmsType; value?: SmsTemplate; onChange: (value: SmsTemplate) => void; audienceKey?: string; serviceTitle?: string | null }) {
  if (audienceKey) return <AudienceTemplatePicker audienceKey={audienceKey} serviceTitle={serviceTitle} value={value} onChange={onChange} />;
  return <GroupTemplatePicker smsType={smsType} value={value} onChange={onChange} />;
}

type Option = { id?: number; title?: string | null; text?: string | null };

function TemplateOption({ option, tags, selected, onSelect }: { option: Option; tags: PlaceHolder[]; selected: boolean; onSelect: () => void }) {
  const text = option.text ?? "";
  return <button type="button" role="radio" aria-checked={selected} data-selected={selected} className={classes.option} onClick={onSelect}>
    <span className={classes.optionHead}>
      <span className={classes.radio} aria-hidden="true">{selected && <IconCheck size={13} stroke={3.5} />}</span>
      <strong>{option.title || "متن پیشنهادی"}</strong>
      <span className={classes.parts}>{fa(smsPartsOf(text, tags))} پیامک</span>
    </span>
    <span className={classes.optionText}><MessageText text={text} tags={tags} /></span>
  </button>;
}

function Chips<T extends string>({ label, options, value, onChange }: { label: string; options: { value: T; label: string }[]; value: T; onChange: (value: T) => void }) {
  return <div className={classes.chips} role="group" aria-label={label}>
    {options.map(option => <button key={option.value} type="button" className={classes.chip} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>{option.label}</button>)}
  </div>;
}

const PAGE = 4;

// Shows the texts four at a time, all on the client; a text already chosen further down stays visible.
function OptionList({ options, tags, value, onSelect, label }: { options: Option[]; tags: PlaceHolder[]; value?: SmsTemplate; onSelect: (option: Option) => void; label: string }) {
  const [count, setCount] = useState(PAGE);
  const selectedAt = options.findIndex(option => option.id === value?.id);
  const visible = Math.max(count, selectedAt + 1);
  const rest = options.length - visible;
  return <Stack gap="sm">
    <div className={classes.optionGrid} role="radiogroup" aria-label={label}>
      {options.slice(0, visible).map(option => <TemplateOption key={option.id} option={option} tags={tags} selected={value?.id === option.id} onSelect={() => onSelect(option)} />)}
    </div>
    {rest > 0 && <Button variant="light" color="violet" onClick={() => setCount(visible + PAGE)}>نمایش بیشتر ({fa(rest)} متن دیگر)</Button>}
  </Stack>;
}

const Legend = () => <p className={classes.legend}>بخش‌های <span className={classes.fillValue}>بنفش</span> برای هر مشتری جداگانه پر می‌شوند.</p>;
const Loading = () => <><Skeleton height={150} radius={16} /><Skeleton height={150} radius={16} /></>;
const Failed = ({ retry }: { retry: () => void }) => <Alert color="red">دریافت متن‌ها انجام نشد.<Button variant="subtle" onClick={retry}>تلاش دوباره</Button></Alert>;

function GroupTemplatePicker({ smsType, value, onChange }: { smsType: SmsType; value?: SmsTemplate; onChange: (value: SmsTemplate) => void }) {
  const [source, setSource] = useState<"suggested" | "personal">("suggested");
  const [search, setSearch] = useState("");
  const groupType = source === "personal" ? SmsGroupTemplateType.Value4 : smsType === SmsType.Value1 ? SmsGroupTemplateType.Value1 : SmsGroupTemplateType.Value0;
  const query = useQuery({ queryKey: ["allSmsTemplate", groupType], queryFn: () => smsTemplateService.getAllSmsTemplate(groupType) });
  const tags = usePreviewTags();
  const templates = query.data?.flatMap(group => group.templates ?? []).filter(template => (source !== "personal" || template.status === SmsTemplateStatus.Value1) && (template.applicableSmsTypes?.includes(smsType) ?? source !== "personal") && (!search || `${template.title} ${template.text}`.includes(search))) ?? [];
  return <Stack gap="md">
    <Chips label="منبع متن‌ها" value={source} onChange={setSource} options={[{ value: "suggested", label: "متن‌های پیشنهادی" }, { value: "personal", label: "قالب‌های من" }]} />
    <TextInput aria-label="جستجوی متن پیام" placeholder="جستجو در عنوان یا متن…" value={search} onChange={e => setSearch(e.currentTarget.value)} leftSection={<IconSearch size={17} />} />
    {templates.length > 0 && <Legend />}
    {query.isLoading ? <Loading /> : query.isError ? <Failed retry={() => void query.refetch()} /> : templates.length > 0 && <OptionList key={`${source}|${search}`} label="متن‌ها" options={templates} tags={tags} value={value} onSelect={option => onChange(option as SmsTemplate)} />}
    {!query.isLoading && !query.isError && !templates.length && <div className={classes.empty}>{search ? "متنی با این عبارت پیدا نشد." : "قالب قابل‌استفاده‌ای در این بخش ندارید."}{source === "personal" && <div><Button mt="sm" color="violet" variant="light" onClick={() => setSource("suggested")}>دیدن متن‌های پیشنهادی</Button></div>}</div>}
  </Stack>;
}

type AudienceFilter = "all" | "short" | "nolink" | "mine";

function AudienceTemplatePicker({ audienceKey, serviceTitle, value, onChange }: { audienceKey: string; serviceTitle?: string | null; value?: SmsTemplate; onChange: (value: SmsTemplate) => void }) {
  const query = useSmartAudienceTemplates(audienceKey);
  const tags = usePreviewTags(serviceTitle ?? null);
  const [filter, setFilter] = useState<AudienceFilter>("all");
  const templates = (query.data ?? []).filter(template => template.status === SmsTemplateStatus.Value1).sort((a, b) => Number(Boolean(b.recommended)) - Number(Boolean(a.recommended)));
  const suggested = templates.filter(template => template.recommended);
  const mine = templates.filter(template => !template.recommended);
  const shown = templates.filter(template => {
    const text = template.text ?? "";
    if (filter === "short") return smsPartsOf(text, tags) <= 2;
    if (filter === "nolink") return !text.includes("websiteLink");
    if (filter === "mine") return !template.recommended;
    return true;
  });
  const choose = (option: Option) => {
    const template = templates.find(item => item.id === option.id);
    if (template) onChange({ id: template.id, title: template.title, text: template.text, status: template.status, applicableSmsTypes: template.smsTypes });
  };
  const filters: { value: AudienceFilter; label: string }[] = [{ value: "all", label: "همه" }, { value: "short", label: "کوتاه‌تر" }, { value: "nolink", label: "بدون لینک" }, ...(mine.length ? [{ value: "mine" as const, label: "متن‌های من" }] : [])];
  if (query.isLoading) return <Stack gap="md"><Loading /></Stack>;
  if (query.isError) return <Failed retry={() => void query.refetch()} />;
  if (!templates.length) return <div className={classes.empty}>برای این مخاطبان متن تأییدشده‌ای پیدا نشد.</div>;
  return <Stack gap="md">
    <div>
      <h2 className={classes.pickerTitle}>{fa(suggested.length || templates.length)} متن آماده برای این مخاطبان</h2>
      <Legend />
    </div>
    <Chips label="فیلتر متن‌ها" value={filter} onChange={setFilter} options={filters} />
    {shown.length ? <OptionList key={filter} label="متن‌های پیشنهادی" options={shown} tags={tags} value={value} onSelect={choose} /> : <div className={classes.empty}>متنی با این فیلتر نیست.<div><Button mt="sm" color="violet" variant="light" onClick={() => setFilter("all")}>دیدن همه</Button></div></div>}
  </Stack>;
}
